
import { computed, defineComponent, reactive, toRef } from 'vue';
import { markRaw } from '@vue/runtime-core';
import _ from 'lodash';
import Utils from '@/utils/QSUtils';
import GridColumnUtils from '@/utils/GridColumnUtils';
import { SourceDataInitials } from '@/constants/SourceDataInitials';
import GridHeader from './GridHeader.vue';
import GridBody from './GridBody.vue';
import GridHeaderData from '@/models/grid/GridHeaderData';
import GridRowData from '@/models/grid/GridRowData';
import BootstrapHelper from '@/utils/bootstrapHelpers';
import { useStore } from 'vuex';
import { Actions, Getters, Mutations, store, formFieldsStore, userSettingsStore } from '@/store';
import { FieldNames } from '@/constants/FieldNames';
import LogUsageManager from '@/manager/LogUsageManager';
import OriginalValuesManager from '@/manager/2.6/OriginalValuesManager';
import CompUtils from '@/models/comp/CompUtils';
import IFeedData from '@/models/comp/IFeedData';
import Loader from '@/components/Loader.vue';
import ModalPRMisMatchAddress from '@/components/2.6/modals/PublicRecordsMisMatchAddress.vue';
import SmartExchangeClaimsManager from '@/manager/2.6/SmartExchangeClaimsManager';
import { newUpsertCompDataRequestByFieldId } from '@/store/2.6/models/IUpsertCompDataRequest';
import LocalStorageManager from '@/manager/LocalStorageManager';
import CompAddressUtils from '@/models/comp/CompAddressUtils';
import { makeGettersWatchable } from '@/utils/StoreWatch';
import SmartExchangeManager from '@/manager/2.6/SmartExchangeManager';
import LocalCompManager from '@/manager/2.6/LocalCompManager';

export default defineComponent({
    components: {
        GridHeader,
        GridBody,
        Loader,
        ModalPRMisMatchAddress,
    },
    data() {
        const previousCompNumber = -1;

        return {
            gridHeight: '100%',
            gridWidth: '100%',
            onlyShowDiscrepancyRows: false,
            gridisLoading: false,
            previousCompNumber,
            isLoading: false,
            modalPRMisMatchAddress: {
                show: false,
                activeComp: {},
                tabIndex: 0,
                component: markRaw(ModalPRMisMatchAddress),
            },
        };
    },
    setup() {
        const store = useStore();
        const userSettings = makeGettersWatchable(userSettingsStore);

        const formFieldsSettings = makeGettersWatchable(formFieldsStore);
        const additionalFields = formFieldsStore.AdditionalFields;
        const formFields = formFieldsStore.FormFields;

        const computedDataSourceColumnOrder = computed(() => userSettings.DataSourceColumnOrder);
        const sourceColumnOrder = _.cloneDeep(computedDataSourceColumnOrder.value);

        const computedPreferredCasing = computed(() => userSettings.PreferredCasing);
        const preferredCasing = computedPreferredCasing.value;

        const computedDisplayDataCascadeColumn = computed(() => userSettings.DisplayDataCascadeColumn);
        const dataCascadeColumn = computedDisplayDataCascadeColumn.value;

        const computedActiveComp = computed(() => store.getters[Getters.CompAddresses.ActiveComp]);
        const activeComp = _.cloneDeep(computedActiveComp.value);

        const emptySources = computed(() => store.getters[Getters.CompAddresses.ActiveCompEmptySourceData]);
        const emptySourceData = emptySources.value;

        const computedSEDetails = computed(() => SmartExchangeClaimsManager.getCurrentClaims());
        const sEDetails = computedSEDetails.value;

        const computedFakeData = computed(() => store.getters[Getters.CompAddresses.GetFakeData]);
        const fakeData = computedFakeData.value;

        const originalValues = OriginalValuesManager.getPropertyByAddress(activeComp.address);

        const headerData: GridHeaderData = {
            cells: [],
        };
        const rowDatas: GridRowData[] = [];

        const state = reactive({
            activeCompData: activeComp,
            formFieldData: formFields,
            additionalFieldData: additionalFields,
            headerData: headerData,
            rowDatas: rowDatas,
            emptySourceData: emptySourceData,
            sEDetails: sEDetails,
        });

        return {
            store,
            userSettings,
            formFieldsSettings,
            activeComp: toRef(state, 'activeCompData'),
            formFields: toRef(state, 'formFieldData'),
            additionalFields: toRef(state, 'additionalFieldData'),
            headerData: toRef(state, 'headerData'),
            rowDatas: toRef(state, 'rowDatas'),
            emptySourceData: toRef(state, 'emptySourceData'),
            sEDetails: toRef(state, 'sEDetails'),
            sourceColumnOrder,
            preferredCasing,
            dataCascadeColumn,
            fakeData,
            originalValues,
        };
    },
    created() {
        // Popover document listener
        BootstrapHelper.popoverDocumentListener();

        window.addEventListener('resize', this.resizeWindowGrid);

        this.userSettings.watch('PreferredCasing', async () => {
            const result = computed(() => this.userSettings.PreferredCasing);
            this.preferredCasing = result.value;
            await this.buildGrid();
        });

        this.userSettings.watch('DisplayDataCascadeColumn', async () => {
            const result = computed(() => this.userSettings.DisplayDataCascadeColumn);
            this.dataCascadeColumn = result.value;
            await this.buildGrid();
        });

        this.userSettings.watch('DataSourceColumnOrder', async () => {
            const result = computed(() => this.userSettings.DataSourceColumnOrder);
            this.sourceColumnOrder = _.cloneDeep(result.value);
            await this.buildGrid();
        });

        this.formFieldsSettings.watch('AdditionalFields', async () => {
            const result = computed(() => this.formFieldsSettings.AdditionalFields);
            this.additionalFields = result.value;
            await this.buildGrid();
        });

        this.formFieldsSettings.watch('FormFields', async () => {
            const result = computed(() => this.formFieldsSettings.FormFields);
            this.formFields = result.value;
            await this.buildGrid();
        });

        this.store.watch(
            (state, getters) => getters[Getters.CompAddresses.OnlyShowDiscrepancyRows],
            async () => {
                const result = computed(() => this.store.getters[Getters.CompAddresses.OnlyShowDiscrepancyRows]);
                this.onlyShowDiscrepancyRows = result.value;
                this.buildGridPayload();
            }
        );

        this.userSettings.watch('PreferredCasing', async () => {
            const result = computed(() => this.userSettings.PreferredCasing);
            this.preferredCasing = result.value;
            await this.buildGrid();
        });

        this.userSettings.watch('DisplayDataCascadeColumn', async () => {
            const result = computed(() => this.userSettings.DisplayDataCascadeColumn);
            this.dataCascadeColumn = result.value;
            await this.buildGrid();
        });

        this.userSettings.watch('DataSourceColumnOrder', async () => {
            const result = computed(() => this.userSettings.DataSourceColumnOrder);
            this.sourceColumnOrder = _.cloneDeep(result.value);
            await this.buildGrid();
        });

        this.store.watch(
            (state, getters) => getters[Getters.CompAddresses.ActiveComp],
            async () => {
                const computedActiveComp = computed(() => this.store.getters[Getters.CompAddresses.ActiveComp]);
                this.activeComp = _.cloneDeep(computedActiveComp.value);
                this.originalValues = OriginalValuesManager.getPropertyByAddress(this.activeComp.address);
                await this.buildGrid();
            },
            { deep: true }
        );

        this.store.watch(
            (state, getters) => getters[Getters.CompAddresses.ActiveCompEmptySourceData],
            async () => {
                const result = computed(() => store.getters[Getters.CompAddresses.ActiveCompEmptySourceData]);
                this.emptySourceData = result.value;
                await this.buildGrid();
            }
        );

        this.store.watch(
            (state, getters) => SmartExchangeClaimsManager.getCurrentClaims(),
            async () => {
                const result = computed(() => SmartExchangeClaimsManager.getCurrentClaims());
                this.sEDetails = result.value;
                await this.buildGrid();
            }
        );

        this.store.watch(
            (state, getters) => getters[Getters.CompAddresses.IsSEColumnCollasped],
            async () => {
                await this.buildGrid();
            }
        );

        this.store.watch(
            (state, getters) => getters[Getters.CompAddresses.IsDBColumnCollasped],
            async () => {
                await this.buildGrid();
            }
        );
    },
    async mounted() {
        await this.buildGrid();
        this.resizeWindowGrid();

        (this as any).eventBus.on('resizeWindowGrid', (e: any) => {
            this.resizeWindowGrid();
        });

        (this as any).eventBus.on('toggleSubCompData', async (e: any) => {
            if (e.isCollapsed) {
                LogUsageManager.hideSubComps(e.initials);
            } else {
                this.isLoading = true;
                LogUsageManager.showSubComps(e.initials);
            }

            if (e.initials === SourceDataInitials.SmartExchange) {
                store.commit(Mutations.CompAddresses.SetSEColumnCollasped, e.isCollapsed);
                await SmartExchangeManager.fetchAndStoreSubComps();
            } else if (e.initials === SourceDataInitials.Database) {
                store.commit(Mutations.CompAddresses.SetDBColumnCollasped, e.isCollapsed);
                await LocalCompManager.fetchAndStoreSubComps();
            }

            this.isLoading = false;
        });
    },
    unmounted() {
        window.removeEventListener('resize', this.resizeWindowGrid);
    },
    methods: {
        async setActiveComp() {
            const result = computed(() => this.store.getters[Getters.CompAddresses.ActiveComp]);
            this.activeComp = _.cloneDeep(result.value);
        },
        async buildGrid() {
            // Commented out gridisLoading because it was causing issues with toggling Total subcomps button on first load
            if (!this.gridisLoading) {
                this.gridisLoading = true;

                await this.setActiveComp();

                // Check if there is a PR mismatched address and no choice has been made
                const address1AndUnitNo = CompAddressUtils.combineAddress1AndUnitNo(this.activeComp.address);
                const misMatchAddress = LocalStorageManager.getMisMatchAddress(address1AndUnitNo);

                if (misMatchAddress && misMatchAddress.usingClosestAddress === null) {
                    this.launchMisMatchPRAddressModal(this.activeComp);
                } else {
                    await this.buildHeaderPayload();
                    await this.buildGridPayload();
                    await this.initializeTooltips();

                    // buildGrid occurring multiple times as the active comp populates with data sources.
                    // On a different comp number, wait until DB & SE data are loaded to prevent repeated logging.
                    if (this.activeComp.sourceDataDB && this.activeComp.sourceDataSE && this.previousCompNumber !== this.activeComp.compNumber) {
                        LogUsageManager.loadedCompTab(this.activeComp);
                        this.previousCompNumber = this.activeComp.compNumber;
                    }
                }

                this.gridisLoading = false;
            }
        },
        async buildHeaderPayload() {
            this.headerData = {
                cells: [],
            };

            if (this.sourceColumnOrder === undefined) return;

            // Field Data
            const fieldDataProps = GridColumnUtils.getHeaderCellData(SourceDataInitials.FieldData);
            this.headerData.cells.push(fieldDataProps);

            // Transfer Data
            const transferDataProps = GridColumnUtils.getHeaderCellData(SourceDataInitials.TransferData);
            this.headerData.cells.push(transferDataProps);

            // ==================================================================
            // Check if subComp is toggled on for current initials and has subComp
            // data.  If so, then append data after parent initials column
            // ==================================================================
            const isSEColumnCollasped = store.getters[Getters.CompAddresses.IsSEColumnCollasped];
            const isDBColumnCollasped = store.getters[Getters.CompAddresses.IsDBColumnCollasped];

            // Get column data based on user active/visible source columns
            for (const settingIdx in this.sourceColumnOrder) {
                // Skip source if user setting is disabled
                if (this.sourceColumnOrder[settingIdx].isHidden) continue;

                const initials = this.sourceColumnOrder[settingIdx].initials;
                const feedDataProps = GridColumnUtils.getHeaderCellData(initials);
                this.headerData.cells.push(feedDataProps);

                // When there is MLS source data we should display the MLS import name.
                if (initials.toLowerCase() === SourceDataInitials.Mls) {
                    GridColumnUtils.updateMlsHeaderName(feedDataProps, this.activeComp.sourceDataMLS);
                }

                if ((initials.toLowerCase() === SourceDataInitials.SmartExchange && !isSEColumnCollasped) || (initials.toLowerCase() === SourceDataInitials.Database && !isDBColumnCollasped)) {
                    if (this.activeComp?.subCompData && Utils.isKeyInObject(this.activeComp.subCompData, initials.toLowerCase())) {
                        const subComps = this.activeComp.subCompData[initials.toLowerCase()];
                        if (subComps.length > 0) {
                            for (const idx in subComps) {
                                const feedDataPropsSubComps = GridColumnUtils.getHeaderCellData(initials);
                                feedDataPropsSubComps.subCompIdx = Number(idx) + 2;
                                this.headerData.cells.push(feedDataPropsSubComps);
                            }
                        }
                    }
                }
            }

            if (this.dataCascadeColumn) {
                // Build column - Data Preference Settings
                const cascadeProps = GridColumnUtils.getHeaderCellData(SourceDataInitials.CascadeData);
                this.headerData.cells.push(cascadeProps);
            }
        },
        async buildGridPayload() {
            this.rowDatas = [];

            if (this.sourceColumnOrder === undefined) return;
            if (this.activeComp === undefined) return;
            if (this.formFields === undefined) return;

            let fieldsFilledInCount = 0;
            let sourceDataCounts = [];

            // Store the Field Filler Count
            store.dispatch(Actions.CompAddresses.ActiveCompFieldFillerCount, fieldsFilledInCount);

            const isSEColumnCollasped = store.getters[Getters.CompAddresses.IsSEColumnCollasped];
            const isDBColumnCollasped = store.getters[Getters.CompAddresses.IsDBColumnCollasped];

            for (const fieldIdx in this.formFields) {
                const fieldId = this.formFields[fieldIdx].fieldId;
                const fieldName = this.formFields[fieldIdx].fieldName;
                const friendlyFieldName = this.formFields[fieldIdx].friendlyFieldName;
                const suggestFieldRule = this.formFields[fieldIdx].suggestFieldRule;
                const fieldCasing = this.formFields[fieldIdx].preferredCasing || this.preferredCasing;
                const flattenedDataPriorities = this.formFields[fieldIdx].flattenedDataPriorities;
                let transferValue = '';
                let transferInitial = '';
                let transferValueMatchSourcesData = true;

                const rowData: GridRowData = {
                    fieldName: fieldName,
                    fieldId: fieldId,
                    photoRow: fieldName === FieldNames.CompPhoto,
                    cells: [],
                };

                // Field Data
                const fieldDataProps = GridColumnUtils.getRowCellData(SourceDataInitials.FieldData);
                fieldDataProps.value = friendlyFieldName;
                rowData.cells.push(fieldDataProps);

                // Transfer Data
                const transferDataProps = GridColumnUtils.getRowCellData(SourceDataInitials.TransferData);

                const compData = this.activeComp.sourceData.find((f: any) => f.fieldName === fieldName);
                transferDataProps.value = compData ? compData.value : '';

                if (fieldName !== FieldNames.CompPhoto) {
                    // Apply casing from global level or Field Level
                    transferDataProps.value = GridColumnUtils.transferValueCasing(transferDataProps.value, fieldCasing);
                }

                transferDataProps.value = GridColumnUtils.formatData(transferDataProps.value, fieldName);

                let fieldRulesApplied = compData ? compData.fieldRulesApplied : false;

                transferDataProps.fieldRulesApplied = fieldRulesApplied;

                transferDataProps.sourceInitial = this.activeComp.sourceData.find((f: any) => f.fieldName === fieldName) ? this.activeComp.sourceData.find((f: any) => f.fieldName === fieldName).sourceInitial : '';
                transferDataProps.fieldNotes = this.activeComp.notesData && this.activeComp.notesData.find((f: any) => f.fieldName === fieldName) ? this.activeComp.notesData.find((f: any) => f.fieldName === fieldName).value : '';
                transferDataProps.friendlyFieldName = friendlyFieldName;
                transferDataProps.suggestFieldRule = suggestFieldRule;

                // If rules has been applied to a blank value, then set the sourceInitial to be User Override on loading of the grid
                if (transferDataProps.fieldRulesApplied && transferDataProps.sourceInitial === '') {
                    transferDataProps.sourceInitial = SourceDataInitials.UserOverride;

                    const request = newUpsertCompDataRequestByFieldId(this.activeComp, fieldId);
                    request.sourceInitial = SourceDataInitials.UserOverride;
                    request.description = 'DataGrid.buildGridPayload()';
                    store.commit(Mutations.CompAddresses.UpsertCompSourceDataByFieldId, request);
                }

                transferValue = transferDataProps.value;
                transferInitial = transferDataProps.sourceInitial;

                rowData.cells.push(transferDataProps);

                // Fields Filled In Count
                if (transferDataProps.sourceInitial !== 'ff' && transferValue) {
                    fieldsFilledInCount++;
                }

                // Get column data based on user active/visible source columns
                for (const settingIdx in this.sourceColumnOrder) {
                    // Skip source if user setting is disabled
                    if (this.sourceColumnOrder[settingIdx].isHidden) continue;

                    const initials = this.sourceColumnOrder[settingIdx].initials;
                    const feedDataProps = GridColumnUtils.getRowCellData(initials);

                    let loadFakeData = false;
                    if (initials === 'SE') {
                        if (!this.sEDetails.isOptedIn && fieldName !== FieldNames.CompPhoto) {
                            loadFakeData = true;
                        }
                    } else {
                        if (Utils.isValueInArray(this.emptySourceData, initials) && initials !== 'DB' && fieldName !== FieldNames.CompPhoto) {
                            loadFakeData = true;
                        }
                    }

                    // Source Data is blank / empty.  Inject dummy value to blur out
                    if (loadFakeData) {
                        let value = '';
                        if (this.fakeData[fieldName]) {
                            value = this.fakeData[fieldName];
                        } else {
                            value = this.generateFakeData(transferValue);
                        }

                        feedDataProps.value = value;
                        feedDataProps.sourceInitial = initials;
                        feedDataProps.isFakeData = true;
                    } else {
                        const result = this.processFeedDataValue({
                            value: this.getFeedDataValue(this.activeComp, initials, fieldName),
                            initials: initials,
                            fieldName: fieldName,
                            transferValue: transferValue,
                            transferOriginalValue: this.originalValues?.find((f: any) => f.fieldId === fieldId) ? this.originalValues.find((f: any) => f.fieldId === fieldId)?.originalValue : '',
                            transferInitial: transferInitial,
                            transferInitialIdx: this.getFeedDataPropertyValueByFieldName(this.activeComp, fieldName),
                            isSubCompColumnData: false,
                        });

                        if (!result.dataMatch && result.value) {
                            transferValueMatchSourcesData = false;
                        }

                        // Insert Assign Additional Fields text for PR column cell row if missing
                        // Assign Additional Fields setting
                        if (fieldName.includes('BL_BLANK') && initials.toLowerCase() === 'pr') {
                            const isDataPointsEmpty = this.additionalFields && this.additionalFields.additionalFields?.find((f: any) => f.blankFieldId === fieldId) ? this.additionalFields.additionalFields.find((f: any) => f.blankFieldId === fieldId)?.dataPoints : null;
                            if (!isDataPointsEmpty) {
                                result.value = 'Assign Additional Fields';
                            }
                        }

                        feedDataProps.value = result.value;
                        feedDataProps.dataMatch = result.dataMatch;
                        feedDataProps.sourceInitial = result.sourceInitial;
                        feedDataProps.dataMatch = result.dataMatch;
                        feedDataProps.dataSelected = result.dataSelected;
                    }
                    rowData.cells.push(feedDataProps);

                    // ==================================================================
                    // Check if subComp is toggled on for current initials and has subComp
                    // data.  If so, then append data after parent initials column
                    // ==================================================================
                    if ((initials.toLowerCase() === 'se' && !isSEColumnCollasped) || (initials.toLowerCase() === 'db' && !isDBColumnCollasped)) {
                        if (this.activeComp?.subCompData?.[initials.toLowerCase()]) {
                            const subComps = this.activeComp?.subCompData?.[initials.toLowerCase()];
                            if (subComps.length > 0) {
                                for (const idx in subComps) {
                                    const subCompFieldValue = subComps?.[idx]?.[fieldName];
                                    const feedDataPropsSubComps = GridColumnUtils.getRowCellData(initials);
                                    const sourceInitialIdx = Number(idx);
                                    const resultSubComp = this.processFeedDataValue({
                                        value: subCompFieldValue,
                                        initials: initials,
                                        fieldName: fieldName,
                                        transferValue: transferValue,
                                        transferOriginalValue: this.originalValues?.find((f: any) => f.fieldId === fieldId) ? this.originalValues.find((f: any) => f.fieldId === fieldId)?.originalValue : '',
                                        transferInitial: transferInitial,
                                        transferInitialIdx: this.getFeedDataPropertyValueByFieldName(this.activeComp, fieldName),
                                        sourceInitialIdx: sourceInitialIdx,
                                        isSubCompColumnData: true,
                                    });

                                    if (!resultSubComp.dataMatch && resultSubComp.value) {
                                        transferValueMatchSourcesData = false;
                                    }

                                    feedDataPropsSubComps.value = resultSubComp.value;
                                    feedDataPropsSubComps.dataMatch = resultSubComp.dataMatch;
                                    feedDataPropsSubComps.sourceInitial = resultSubComp.sourceInitial;
                                    feedDataPropsSubComps.dataMatch = resultSubComp.dataMatch;
                                    feedDataPropsSubComps.dataSelected = resultSubComp.dataSelected;
                                    feedDataPropsSubComps.sourceInitialIdx = sourceInitialIdx;

                                    rowData.cells.push(feedDataPropsSubComps);
                                }
                            }
                        }
                    }
                }

                if (this.dataCascadeColumn) {
                    // Data Preference Settings
                    const cascadeDataProps = GridColumnUtils.getRowCellData(SourceDataInitials.CascadeData);
                    cascadeDataProps.value = GridColumnUtils.styleFlattenedDataPriorities(flattenedDataPriorities);
                    rowData.cells.push(cascadeDataProps);
                }

                if (this.onlyShowDiscrepancyRows && !transferValueMatchSourcesData) {
                    this.rowDatas.push(rowData);
                } else if (!this.onlyShowDiscrepancyRows) {
                    this.rowDatas.push(rowData);
                }
            }

            // Store the Field Filler Count
            store.dispatch(Actions.CompAddresses.ActiveCompFieldFillerCount, fieldsFilledInCount);

            // Get active comps source data counts
            for (const settingIdx in this.sourceColumnOrder) {
                const initials = this.sourceColumnOrder[settingIdx].initials;
                if (initials.toLowerCase() === 'se' || initials.toLowerCase() === 'db') {
                    const result = this.getFeedData(this.activeComp, initials);
                    sourceDataCounts.push(result);
                }
            }

            store.dispatch(Actions.CompAddresses.ActiveCompSourceCounts, sourceDataCounts);
        },
        processFeedDataValue(data: any) {
            let value = data.value;
            const initials = data.initials;
            const fieldName = data.fieldName;
            const transferValue = data.transferValue;
            const transferOriginalValue = data.transferOriginalValue;
            const transferInitial = data.transferInitial;
            const transferInitialIdx = data.transferInitialIdx;
            const sourceInitialIdx = data.sourceInitialIdx;
            const isSubCompColumnData = data.isSubCompColumnData;

            let dataSelected = false;
            let dataMatch = false;
            value = GridColumnUtils.formatData(value, fieldName);
            if (value) {
                // If transferOriginalValue is not blank then you for comparsion
                let isMatch = false;

                if (transferOriginalValue !== '') {
                    isMatch = transferOriginalValue.toLowerCase() === value.toLowerCase() ? true : false;
                } else {
                    isMatch = transferValue.toLowerCase() === value.toLowerCase() ? true : false;
                }
                dataMatch = isMatch;

                // if (fieldName === 'HS_PRIOR2' && this.onlyShowDiscrepancyRows) {
                //     console.log('====================================');
                //     console.log('sourceInitialIdx', sourceInitialIdx);
                //     console.log('transferInitialIdx', transferInitialIdx);
                //     console.log('transferInitial', transferInitial);
                //     console.log('initials', initials);
                //     console.log('dataMatch', dataMatch);
                //     console.log('transferValue', transferValue);
                //     console.log('value', value);
                //     console.log('isSubCompColumnData', isSubCompColumnData);
                // }

                if (isSubCompColumnData) {
                    // Check SubComp Column
                    if ((transferInitialIdx !== undefined || transferInitialIdx !== null) && dataMatch && transferInitialIdx === sourceInitialIdx) {
                        dataSelected = transferInitial.toLowerCase() === initials.toLowerCase() ? true : false;
                    }
                } else {
                    // Check Primary Column
                    if ((transferInitialIdx === undefined || transferInitialIdx === null) && transferInitial && initials && dataMatch) {
                        dataSelected = transferInitial.toLowerCase() === initials.toLowerCase() ? true : false;
                    }
                }
            }

            return {
                value: value,
                sourceInitial: initials,
                dataMatch: dataMatch,
                dataSelected: dataSelected,
            };
        },
        resizeWindowGrid() {
            const windowHeight = window.innerHeight;
            const windowWidth = window.innerWidth;

            const gridPosition = Utils.getElementPositionById('grid');
            const gridMarginCss = Utils.getElementStylesById('grid', 'margin-left');
            const gridMargin = Utils.convertStringToNumber(gridMarginCss) | 0;
            const guidePosition = Utils.getElementPositionById('guide');
            const footerPosition = Utils.getElementPositionById('footer');

            const gridH = windowHeight - (gridPosition.top + guidePosition.height + footerPosition.height);
            const gridW = windowWidth - gridMargin * 2;

            this.gridHeight = `${gridH}px`;
            this.gridWidth = `${gridW}px`;
        },
        getFeedDataValue(activeComp: any, initials: string, fieldName: string): string {
            const sourceData = CompUtils.getDataSourceSourceData(activeComp, initials);
            if (sourceData && Utils.isKeyInObject(sourceData, fieldName)) {
                return sourceData[fieldName];
            }
            return '';
        },
        getFeedDataPropertyValueByFieldName(activeComp: any, fieldName: string) {
            const result = computed(() =>
                store.getters[Getters.CompAddresses.GetCompSourceDataValueByFieldName]({
                    comp: activeComp,
                    fieldName: fieldName,
                    name: 'sourceInitialIdx',
                })
            );

            return result.value;
        },
        getFeedData(activeComp: any, initials: string) {
            const sourceProperyName = `sourceData${initials}`;
            let value: IFeedData = {
                initials: initials.toLowerCase(),
                isPeerComp: false,
                peerCount: 0,
                yourCount: 0,
                records: 0,
                reportUseCount: 0,
            };
            if (Utils.isKeyInObject(activeComp, sourceProperyName)) {
                if (!Utils.isObjectEmpty(activeComp[sourceProperyName])) {
                    value.isPeerComp = activeComp[sourceProperyName].isPeerComp;
                    value.peerCount = activeComp[sourceProperyName].peerCount;
                    value.yourCount = activeComp[sourceProperyName].yourCount;
                    value.records = activeComp[sourceProperyName].records;
                    value.reportUseCount = activeComp[sourceProperyName].reportUseCount;
                }
            }
            return value;
        },
        isFeedDataEmpty(activeComp: any, initials: string): boolean {
            const sourceData = CompUtils.getDataSourceSourceData(activeComp, initials);
            return !sourceData || Utils.isObjectEmpty(sourceData);
        },
        generateFakeData(transferValue: any): string {
            let value = transferValue;
            if (value) {
                value = Utils.ranStr(value.length, 'lower');
            } else {
                value = Utils.ranStr(7, 'lower');
            }
            return value;
        },
        async initializeTooltips() {
            BootstrapHelper.tooltipInitialize();
        },
        launchMisMatchPRAddressModal(activeComp: any) {
            this.modalPRMisMatchAddress.show = true;
            this.modalPRMisMatchAddress.activeComp = activeComp;
            this.modalPRMisMatchAddress.tabIndex = activeComp.compNumber;
        },
    },
});
